import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  closestCenter,
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensor,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import Label from '../../../../../Label';
import CardDesignerLayer from './components/CardDesignerLayer';
import { isElementCardBackground } from '../CardDesignerBackgroundControls/helpers';
import { isElementCardForeground } from '../CardDesignerForegroundControls/helpers';

const propTypes = {
  className: PropTypes.string,
  elements: PropTypes.array,
  onCardElementsUpdate: PropTypes.func.isRequired,
  onCardElementSelect: PropTypes.func.isRequired,
};

const CardDesignerLayerControls = ({
  className,
  elements,
  selectedCardElement,
  onCardElementsUpdate,
  onCardElementSelect,
}) => {
  const zindexSortedElements = useMemo(
    () =>
      [...elements].sort((a, b) => {
        if (a.cardElement?.z_index > b.cardElement?.z_index) return -1;

        if (b.cardElement?.z_index > a.cardElement?.z_index) return 1;

        return 0;
      }),
    [elements],
  );

  const reorderElements = async orderedElements => {
    const data = {};
    orderedElements.forEach((elem, idx) => {
      data[elem.cardElement.id] = {
        z_index: orderedElements.length - 1 - idx,
      };
    });
    await onCardElementsUpdate({
      cardElements: orderedElements.map(elem => elem.cardElement),
      data,
    });
  };

  const onDragEnd = ({ active, over }) => {
    if (!active || !over) {
      return;
    }

    if (active?.disabled || over?.disabled) {
      return;
    }

    if (
      isElementCardBackground(active?.data?.current?.element) ||
      isElementCardBackground(over?.data?.current?.element)
    ) {
      return;
    }

    if (active.id !== over.id) {
      const oldIndex = zindexSortedElements.findIndex(
        ({ cardElement }) => cardElement.id === active.id,
      );
      const newIndex = zindexSortedElements.findIndex(
        ({ cardElement }) => cardElement.id === over.id,
      );
      const reorderedElements = arrayMove(
        zindexSortedElements,
        oldIndex,
        newIndex,
      );

      reorderElements(reorderedElements);
    }
  };

  // Drag and Drop Context Related
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      delay: 125,
      tolerance: 5,
    },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 200,
      tolerance: 5,
    },
  });

  const classes = classNames({
    [className]: !!className,
  });

  return (
    <div className={classes}>
      <div className="card-designer-layer-controls">
        <Label id="layer-control">Drag tiles to rearrange layers</Label>
        <DndContext
          onDragEnd={onDragEnd}
          sensors={[mouseSensor, touchSensor]}
          collisionDetection={closestCenter}
        >
          <SortableContext
            items={zindexSortedElements.map(
              ({ cardElement }) => cardElement?.id,
            )}
            strategy={verticalListSortingStrategy}
          >
            {zindexSortedElements.map(element => (
              <CardDesignerLayer
                element={element}
                onClick={() => {
                  if (
                    !isElementCardBackground(element) &&
                    !isElementCardForeground(element)
                  ) {
                    onCardElementSelect(element?.cardElement?.id);
                  }
                }}
                isSelected={
                  selectedCardElement?.cardElement?.id ===
                  element?.cardElement?.id
                }
                isDraggable={!isElementCardBackground(element)}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};

CardDesignerLayerControls.propTypes = propTypes;

export default CardDesignerLayerControls;
